import api from '../../../../utils/Api'
import { ID } from '../../../../../_metronic/helpers'

export interface PaymentGateway {
  id: number
  name: string
  gatewayType: 'bank' | 'cash' | 'e_wallet' | 'advance'
  code: string
  note?: string
  forPaying?: boolean
  forReceiving?: boolean
  accountNo?: string
  accountName?: string
  bankAccountType?: 'saving' | 'current' | 'fixed' | 'fixed_3_months' | 'fixed_6_months' | 'fixed_1_year'
  bankName?: string
  branch?: string
  branchAccountNo?: string
  branchName?: string
  branchNo?: string
  providerType?: string
  providerName?: string
  accountCode?: string
  balance: number
  // เงินสดย่อย (account code 201314) เท่านั้น — ผู้ถือเป็นพนักงาน สาขาละ 1 คน
  userId?: number | null
  branchId?: number | null
  imprestAmount?: number | null
  holder?: { id: number; fullName?: string; firstName?: string; lastName?: string } | null
  branchItem?: { id: number; name: string; code: string } | null
  createdAt: string
  updatedAt: string
}

/** บัญชีเงินสดย่อยฝั่งหนี้สิน ใช้ดูว่าช่องทางสำรองรับจ่ายใบไหนเป็นเงินสดย่อย */
export const PETTY_CASH_ACCOUNT_CODE = '201314'

/** สรุปเงินสดย่อยรายผู้ถือ จาก GET account/payment-gateway/petty-cash-holders */
export interface PettyCashHolderSummary {
  id: number
  code: string
  name: string
  holderId: number | null
  holderName: string | null
  branchId: number | null
  branchName: string | null
  imprestAmount: number
  outstanding: number
  cashOnHand: number
}

export interface PaymentGatewayQueryResponse {
  data: PaymentGateway[]
  payload: {
    message: string
    errors: any
    pagination: {
      page: number
      items_per_page: number
      count: number
      total: number
      links: any
    }
  }
}

export interface CreatePaymentGatewayRequest {
  name: string
  gatewayType: 'bank' | 'cash' | 'e_wallet' | 'advance'
  note?: string
  forPaying?: boolean
  forReceiving?: boolean
  accountNo?: string
  accountName?: string
  bankAccountType?: 'saving' | 'current' | 'fixed' | 'fixed_3_months' | 'fixed_6_months' | 'fixed_1_year'
  bankName?: string
  branch?: string
  branchAccountNo?: string
  branchName?: string
  branchNo?: string
  providerType?: string
  providerName?: string
  accountCode?: string
  accountCodeId?: number
  // เงินสดย่อย — ใช้ชื่อ holderBranchId ไม่ใช่ branchId เพราะฝั่ง API มี `?branchId=`
  // ใน query string ที่จะทับค่าใน body
  userId?: number | null
  holderBranchId?: number | null
  imprestAmount?: number | null
}

// API Functions using api.request pattern
export const getPaymentGateways = (params?: any): Promise<PaymentGateway[]> => {
  return new Promise((resolve, reject) => {
    api.request('account/payment-gateway', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, params)
  })
}

export const getPaymentGatewayById = (id: any): Promise<PaymentGateway | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`account/payment-gateway/${id}`, (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        resolve(undefined)
      }
    })
  })
}

/**
 * สรุปเงินสดย่อยรายผู้ถือ: วงเงิน / ยอดค้างเบิกรอจ่ายคืน / เงินสดที่ควรเหลือในมือ
 * ยอดค้างเบิกฝั่ง API อ่านจากสมุดบัญชี (บรรทัด 201314 ที่ผูก gateway) ไม่ใช่ยอดคงเหลือของช่องทาง
 */
export const getPettyCashHolders = (): Promise<PettyCashHolderSummary[]> => {
  return new Promise((resolve, reject) => {
    api.request('account/payment-gateway/petty-cash-holders', (data: any, success: boolean) => {
      if (success) {
        resolve(data || [])
      } else {
        reject(data)
      }
    })
  })
}

/** รายชื่อพนักงาน ไว้เลือกเป็นผู้ถือเงินสดย่อย */
export const getEmployeeOptions = (): Promise<{ value: number; label: string }[]> => {
  return new Promise((resolve, reject) => {
    api.request('user', (data: any, success: boolean) => {
      if (success) {
        resolve((data || []).map((u: any) => ({ value: u.id, label: u.fullName })))
      } else {
        reject(data)
      }
    }, { forDropdown: true })
  })
}

/**
 * รายชื่อสาขา ไว้เลือกสาขาที่ผู้ถือดูแล
 *
 * ต้องส่ง `forDropdown` ไปด้วย ไม่งั้น API จะ paginate แล้วได้มาแค่ 20 สาขาแรก
 * (เจอตอนเทสบน prod — สาขาที่อยู่หน้าถัดไปหาไม่เจอในช่องเลือก)
 * `forDropdown` ยังกรองเอาเฉพาะสาขาที่ยังเปิดอยู่ให้ด้วย
 */
export const getBranchOptions = (): Promise<{ value: number; label: string }[]> => {
  return new Promise((resolve, reject) => {
    api.request('branch', (data: any, success: boolean) => {
      if (success) {
        const rows = data?.data ?? data ?? []
        resolve(rows.map((b: any) => ({ value: b.id, label: `${b.code} - ${b.name}` })))
      } else {
        reject(data)
      }
    }, { forDropdown: true })
  })
}

/** ใบ PVC ที่ยังรอจ่ายคืนให้ผู้ถือคนหนึ่ง */
export interface PettyCashOutstandingRow {
  id: number
  code: string
  date: string
  refDoc: string | null
  accountDesc: string | null
  amount: number
}

export const getPettyCashOutstanding = (
  paymentGatewayId: number
): Promise<PettyCashOutstandingRow[]> => {
  return new Promise((resolve, reject) => {
    api.request('account/petty-cash/outstanding', (data: any, success: boolean) => {
      if (success) {
        resolve(data || [])
      } else {
        reject(data)
      }
    }, { paymentGatewayId })
  })
}

export interface PettyCashReimburseRequest {
  paymentGatewayId: number
  fromPaymentGatewayId: number
  date: string
  accountBookIds: number[]
  note?: string
  feeItems?: { accountCode?: string; accountName?: string; amount: number }[]
}

/** จ่ายคืนเงินสดย่อย — ดึง PVC หลายใบมาล้างทีเดียว ลงเล่ม PV */
export const reimbursePettyCash = (
  data: PettyCashReimburseRequest
): Promise<{ code: string; accountBookId: number; amount: number }> => {
  return new Promise((resolve, reject) => {
    api.request('account/petty-cash/reimburse', (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'POST')
  })
}

/** ใบจ่ายคืนที่เคยทำให้ผู้ถือคนหนึ่ง */
export interface PettyCashReimbursementRow {
  id: number
  code: string
  date: string
  accountDesc: string | null
  amount: number
  cancelled: boolean
}

export const getPettyCashReimbursements = (
  paymentGatewayId: number
): Promise<PettyCashReimbursementRow[]> => {
  return new Promise((resolve, reject) => {
    api.request('account/petty-cash/reimbursements', (data: any, success: boolean) => {
      if (success) {
        resolve(data || [])
      } else {
        reject(data)
      }
    }, { paymentGatewayId })
  })
}

/**
 * ยกเลิกใบจ่ายคืน — ฝั่ง API จะกลับรายการตาม ADR 0001 เปิดใบ PVC กลับมาค้างเบิก
 * และคืนยอดช่องทางการเงินให้ครบในครั้งเดียว
 */
export const cancelPettyCashReimbursement = (
  accountBookId: number,
  reason?: string
): Promise<{ code: string | null; reopenedCodes: string[] }> => {
  // code = เลขเล่มกลับรายการ เป็น null เมื่อใบจ่ายคืนยังไม่เคยอนุมัติ (ไม่ต้องกลับรายการ)
  return new Promise((resolve, reject) => {
    api.request(`account/petty-cash/reimburse/${accountBookId}/cancel`, (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form({ ...(reason ? { reason } : {}) }), 'POST')
  })
}

export const createPaymentGateway = (data: CreatePaymentGatewayRequest): Promise<PaymentGateway | undefined> => {
  return new Promise((resolve, reject) => {
    api.request('account/payment-gateway', (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'POST')
  })
}

export const updatePaymentGateway = (id: ID, data: CreatePaymentGatewayRequest): Promise<PaymentGateway | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`account/payment-gateway/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'PATCH')
  })
}

export const deletePaymentGateway = (id: ID): Promise<void> => {
  return new Promise((resolve, reject) => {
    api.request(`account/payment-gateway/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve()
      } else {
        reject(response)
      }
    }, {}, 'DELETE')
  })
}

// Cheque interfaces based on actual API response
export interface Cheque {
  id: number
  chequeNo: string
  chequeName: string // ชื่อผู้รับ/จ่าย
  issuedDate: string // วันที่เช็ค
  effectiveDate: string // วันรับ/จ่าย
  amount: number
  chequeBranch: string
  accountNo: string
  documentRef: string
  documentRefId: number
  documentRefType: string
  // เช็คใบนี้มาจาก IncomeDocumentPayment ครั้งไหน — ใช้ยกเลิก/ถอยสถานะแบบ cascade (core/paymentCancelFlow.ts)
  incomeDocumentPaymentId: number | null
  bankName: string
  receivedBy: string // Cash or cheque
  paidBankAccountNo?: string | null
  paidBankAccountName?: string | null
  paidBankAccountType?: string | null
  paidBankName?: string | null
  paidDate?: string | null
  // ChequeStates: not_filled_yet, ready, completed, voided (ยกเลิกเช็ค — ใช้ร่วมกันทั้งสองทิศทาง)
  // returned/cancelled คือของเดิมที่เลิกใช้แล้ว (ADR 0002 ฝั่ง ldc-api) เหลือไว้แค่ให้แถวเก่า cast ผ่าน
  state: string
  returnedDate?: string | null
  returnedReason?: string | null
  cancelledDate?: string | null
  cancelledReason?: string | null
  chequeType: string // 'received' | 'payment'
  createdAt: string
  updatedAt: string
  sellerReceiverName: string
}

// จำนวนและยอดรวมแยกตามสถานะ คำนวณที่ API ตามตัวกรองที่ใช้อยู่ (ไม่รวมตัวกรองสถานะเอง)
export type ChequeStateSummary = Record<string, { count: number, amount: number }>

// ยอดรวมแยกตามธนาคาร ใช้ตอนออกรายงานโดยไม่เลือกธนาคาร (ต้องแยก subtotal ต่อธนาคาร)
export interface ChequeBankSummaryRow {
  bankName: string
  count: number
  amount: number
}

export interface ChequeQueryResponse {
  meta: {
    total: number
    per_page: number
    current_page: number
    last_page: number
    first_page: number
    first_page_url: string
    last_page_url: string
    next_page_url: string | null
    previous_page_url: string | null
  }
  data: Cheque[]
  summary: ChequeStateSummary
  bankSummary: ChequeBankSummaryRow[]
}

export interface ChequeQueryParams {
  startDate?: string
  endDate?: string
  chequeType: 'received' | 'payment'
  page?: number
  perPage?: number
  state?: string
  partyName?: string
  // รายชื่อธนาคารดิบคั่นด้วย comma (สะกดตรงกับที่เก็บใน DB) — คำนวณจาก findBankByName ฝั่งหน้าจอ
  // เพราะ API ไม่รู้จักชื่อธนาคาร ดู core/chequeReport.ts groupBanksByCanonicalName
  bankNames?: string
  search?: string
}

export interface ChequeFilterOptions {
  parties: string[]
  // ไม่มี banks — dropdown ธนาคารใช้ลิสต์เต็มจาก constants/Banks.tsx (ดู chequeReport.ts ALL_BANK_OPTIONS)
}

export interface CreateChequeRequest {
  chequeNo: string
  chequeName: string
  issuedDate: string
  effectiveDate: string
  amount: number
  chequeBranch: string
  accountNo: string
  bankName?: string
  receivedBy?: string
  paidBankAccountNo?: string | null
  paidBankAccountName?: string | null
  paidBankAccountType?: string | null
  paidBankName?: string | null
  state: string
  chequeType: 'received' | 'payment'
}

export interface UpdateChequeRequest {
  chequeNo?: string
  chequeName?: string
  issuedDate?: string
  effectiveDate?: string
  amount?: number
  chequeBranch?: string
  accountNo?: string
  bankName?: string
  receivedBy?: string
  paidBankAccountNo?: string | null
  paidBankAccountName?: string | null
  paidBankAccountType?: string | null
  paidBankName?: string | null
  paidDate?: string | null
  state?: string
  returnedDate?: string | null
  returnedReason?: string | null
  cancelledDate?: string | null
  cancelledReason?: string | null
  chequeType?: string
}

// Cheque API Functions
export const getCheques = (params: ChequeQueryParams): Promise<ChequeQueryResponse> => {
  return new Promise((resolve, reject) => {
    api.request('account/cheque', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, params)
  })
}

// รายชื่อ supplier/ผู้จ่ายเงิน และธนาคาร ที่มีข้อมูลจริง สำหรับ populate ตัวกรองแบบเลือก (ไม่ใช่พิมพ์ค้นหาอิสระ)
export const getChequeFilterOptions = (chequeType: 'received' | 'payment'): Promise<ChequeFilterOptions> => {
  return new Promise((resolve, reject) => {
    api.request('account/cheque-filter-options', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, { chequeType })
  })
}

export const getChequeById = (id: ID): Promise<Cheque | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`account/cheque/${id}`, (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        resolve(undefined)
      }
    })
  })
}

export const createCheque = (data: CreateChequeRequest): Promise<Cheque | undefined> => {
  return new Promise((resolve, reject) => {
    api.request('account/cheque', (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'POST')
  })
}

export const updateCheque = (id: ID, data: UpdateChequeRequest): Promise<Cheque | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`account/cheque/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'PATCH')
  })
}

export const deleteCheque = (id: ID): Promise<void> => {
  return new Promise((resolve, reject) => {
    api.request(`account/cheque/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve()
      } else {
        reject(response)
      }
    }, {}, 'DELETE')
  })
}

// ยกเลิกเช็ค / ถอยสถานะ — cascade ไป PV/RV, เอกสารต้นทาง, ใบหัก ณ ที่จ่าย ดู core/paymentCancelFlow.ts
export interface AccountBookLite {
  id: number
  bookType: string
  state: string // AccountBookStates: draft, pending_approval, approved, ...
  refDoc: string | null
}

// เอกสารอาจมีเล่ม PV/RV ของ "รุ่นก่อน ๆ" ค้างอยู่ได้ (ยกเลิกไปแล้วแต่เก็บไว้ตาม ADR 0001)
// endpoint นี้คืนเฉพาะเล่มที่ยัง "มีชีวิต" (ยังไม่เคยถูกกลับรายการ) เสมอ
export const getAccountBookByCode = (code: string): Promise<AccountBookLite | null> => {
  return new Promise((resolve, reject) => {
    api.request(`account/income-document/${code}/account-book`, (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    })
  })
}

export const getAccountBookByPayment = (paymentId: ID): Promise<AccountBookLite | null> => {
  return new Promise((resolve, reject) => {
    api.request(`account/income-document-payment/${paymentId}/account-book`, (data: any, success: boolean) => {
      if (success) resolve(data)
      else reject(data)
    })
  })
}

export interface CancelPaymentResult {
  paymentId: number
  incomeDocumentId: number
  incomeDocumentState: string
  incomeDocumentPreviousState: string
  voidedChequeIds: number[]
  reversalAccountBookIds: number[]
  cancelledAccountBookIds: number[]
  cancelledWithholdingTaxIds: number[]
  message: string
}

export const cancelIncomeDocumentPayment = (paymentId: ID, reason: string): Promise<CancelPaymentResult> => {
  return new Promise((resolve, reject) => {
    api.request(`account/income-document-payment/${paymentId}/cancel`, (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, { reason }, 'POST')
  })
}

export const revertAccountBookState = (accountBookId: ID, state: string, reason: string): Promise<AccountBookLite> => {
  return new Promise((resolve, reject) => {
    api.request(`account/account-book/update-state/${accountBookId}`, (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, { state, reason }, 'POST')
  })
}

// PaymentGatewayTransaction interfaces based on backend model
export interface PaymentGatewayTransaction {
  id: number
  amount: number
  baseAmount?: number // เงินหลักก่อนปรับปรุง (ใหม่)
  receiveAmount?: number // เงินที่ปลายทางได้รับจริง (ใหม่)
  paymentGatewayId: number
  toPaymentGatewayId?: number // ไอดีของ payment gateway ปลายทาง (ใหม่)
  incomeDocumentId: number
  incomeDocumentType: string
  documentRef?: string
  contactName: string
  txType: 'transfer_in' | 'transfer_out' | 'receive' | 'payment' | 'transfer_to_cheque'
  note: string
  state: 'pending' | 'completed'
  date: string
  createdAt: string
  updatedAt: string
  feeItems?: FeeItem[] // รายการค่าธรรมเนียม (ถ้ามี)
  incomeDocument?: {
    id: number
    documentNo: string
    date: string
    // Add other fields as needed
  }
  paymentGateway?: PaymentGateway
  toPaymentGateway?: PaymentGateway // Payment gateway ปลายทาง (ใหม่)
}

export interface PaymentGatewayTransactionQueryResponse {
  meta: {
    total: number
    per_page: number
    current_page: number
    last_page: number
    first_page: number
    first_page_url: string
    last_page_url: string
    next_page_url: string | null
    previous_page_url: string | null
  }
  data: PaymentGatewayTransaction[]
}

export interface PaymentGatewayTransactionQueryParams {
  paymentGatewayId: number
  startDate?: string
  endDate?: string
  page: number
}

export interface UpdatePaymentGatewayTransactionRequest {
  state: 'pending' | 'completed'
}

export interface FeeItem {
  type: 'deduct' | 'add'
  accountCode: string
  amount: number
  accountName?: string
}

export interface CreatePaymentGatewayTransactionRequest {
  amount: number
  paymentGatewayId: number
  contactName: string
  txType: 'transfer_in' | 'transfer_out' | 'receive' | 'payment' | 'transfer_to_cheque'
  note: string
  date: string
  documentRef?: string
  // สำหรับการโอนระหว่าง gateway
  toPaymentGatewayId?: number
  // สำหรับการโอนไปยังเช็ค
  chequeNo?: string
  chequeName?: string
  effectiveDate?: string
  chequeBranch?: string
  accountNo?: string
  bankName?: string
  // สำหรับ fee items
  feeItems?: FeeItem[]
  // สำหรับแยก expense transactions
  separateExpenseTransactions?: boolean
  /**
   * เจตนาของการโอน — 'petty_cash_float' = ตั้ง/เติมวงเงินเงินสดย่อย (Dr 101102 / Cr ต้นทาง)
   * ต้องส่งมาจากหน้าจอเท่านั้น ระบบไม่เดาเอง เพราะการจ่ายคืนก็โอนเข้าช่องทางเดียวกัน
   * แต่ลง Dr 201314 คนละทาง
   */
  purpose?: string
}

// PaymentGatewayTransaction API Functions
export const getPaymentGatewayTransactions = (params: PaymentGatewayTransactionQueryParams): Promise<PaymentGatewayTransactionQueryResponse> => {
  return new Promise((resolve, reject) => {
    api.request('account/payment-gateway-transaction', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, params)
  })
}

export const createPaymentGatewayTransaction = (data: CreatePaymentGatewayTransactionRequest): Promise<PaymentGatewayTransaction | undefined> => {
  return new Promise((resolve, reject) => {
    api.request('account/payment-gateway-transaction', (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'POST')
  })
}

export const updatePaymentGatewayTransaction = (id: ID, data: UpdatePaymentGatewayTransactionRequest): Promise<PaymentGatewayTransaction | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`account/payment-gateway-transaction/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(data), 'PATCH')
  })
}

// WithholdingTax enums matching backend
export enum WithholdingTaxTypes {
  NORMAL = 'normal',
  E_WTH = 'e_wht'
}

export enum WithholdingTaxActionTypes {
  MAKER = 'maker', //เป็นผู้หัก
  TAKER = 'taker'  //ถูกหัก
}

export enum PndTypes {
  PND1 = '1',
  PND2 = '2', 
  PND3 = '3',
  PND53 = '53',
  PND54 = '54'
}

export enum TaxConditions {
  DEDUCT_AT_SOURCE = 'หัก ณ ที่จ่าย',
  ISSUE_FOREVER = 'ออกให้ตลอดไป',
  ISSUE_ONCE = 'ออกให้ครั้งเดียว'
}

// WithholdingTax interfaces based on backend models
export interface WithholdingTax {
  id: number
  code: string
  date: string
  file?: {
    url?: string
    name?: string
  } | null
  type: WithholdingTaxTypes
  actionType: WithholdingTaxActionTypes
  isTrash: boolean
  isDeducted?: number
  incomeDocumentPaymentId: number
  amount: number
  whTax: number
  taxPrice: number
  note?: string
  accountCode?: string | null
  accountCodeName?: string | null
  pndType?: PndTypes
  /** ประเภทเงินได้ — ช่องที่ 12 ของไฟล์ ภ.ง.ด. เลือกตอนบันทึก ไม่ได้คำนวณย้อนหลัง */
  incomeType?: string | null
  taxCondition?: TaxConditions
  createdAt: string
  updatedAt: string
  incomeDocumentPayment?: any
}

export interface WithholdingTaxQueryResponse {
  meta: {
    total: number
    per_page: number
    current_page: number
    last_page: number
    first_page: number
    first_page_url: string
    last_page_url: string
    next_page_url: string | null
    previous_page_url: string | null
  }
  data: WithholdingTax[]
}

export interface WithholdingTaxQueryParams {
  startDate?: string
  endDate?: string
  actionType: WithholdingTaxActionTypes
  pnd?: number
  type?: WithholdingTaxTypes
  page?: number
  /** ค้นเลขที่เอกสารภาษี เลขที่เอกสารอ้างอิง หรือชื่อผู้ขาย */
  s?: string
}

export interface UpdateWithholdingTaxRequest {
  note?: string
  date?: string
  code?: string
  file?: File
  isDeducted?: number
  incomeType?: string
}

/**
 * ประเภทเงินได้ที่ใช้จริงในไฟล์ ภ.ง.ด. ของฝ่ายบัญชี — ไม่ได้ตั้งขึ้นเอง
 *
 * ต้องตรงกับ `app/Constants/WithholdingIncomeType.ts` ฝั่ง ldc-api — API ใช้รายการนั้นตัดสินว่า
 * ประเภทของรหัสบัญชีใช้เป็นค่าเริ่มต้นได้ไหม
 */
export const WITHHOLDING_INCOME_TYPES = [
  // ฝั่งค่าใช้จ่าย — จาก "ผังบัญชี หัก ณ ที่จ่าย.xlsx" ของฝ่ายบัญชี
  'ค่าบริการ',
  'ค่าเช่า',
  'ค่าโฆษณา',
  'ค่าขนส่ง',
  'ค่าเบี้ยประกัน',
  'ค่าคอมมิชชั่น',
  'ค่าสอบบัญชี',
  // ฝั่งเงินเดือน — จากไฟล์ ภ.ง.ด.3 ที่ยื่นจริง (จำนวนบรรทัดที่ใช้ในวงเล็บ)
  'เงินเดือน / ค่าจ้างรายวัน',   // 97
  'เบี้ยขยัน Incentive',        // 52
  'เบี้ยเลี้ยง',                 // 35
  'โอที',                       // 31
  'ค่า Commission',             // 22
  'เงินได้อื่นๆ',                // 19
  'ค่าล่วงเวลา',                 // 16
  'เบี้ยเลี้ยง (ออกตจว.)',       // 3
  'ค่าเดินทาง',                  // 2
]

export interface ExtraWithholdingTaxItem {
  id: number
  incomeDocumentPaymentId: number
  whTax: number
  amount: number
  whTaxPrice: number
  createdAt: string
  updatedAt: string
  incomeDocumentPayment?: {
    id: number
    incomeDocument?: {
      id: number
      documentNo: string
      customerName?: string
      vendorName?: string
    }
  }
}

// WithholdingTax API Functions
export const getWithholdingTaxes = (params: WithholdingTaxQueryParams): Promise<any> => {
  return new Promise((resolve, reject) => {
    api.request('account/withholding-tax', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, params)
  })
}

export const getWithholdingTaxById = (id: ID): Promise<WithholdingTax | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`account/withholding-tax/${id}`, (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        resolve(undefined)
      }
    })
  })
}

export const getWithholdingTaxByCode = (code: string): Promise<WithholdingTax | undefined> => {
  return new Promise((resolve, reject) => {
    api.request(`account/withholding-tax/${code}`, (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        resolve(undefined)
      }
    })
  })
}

export const updateWithholdingTax = (id: ID, data: UpdateWithholdingTaxRequest): Promise<WithholdingTax | undefined> => {
  return new Promise((resolve, reject) => {
    const files = data.file ? { file: data.file } : undefined
    const formData = { ...data }
    delete formData.file
    
    api.request(`account/withholding-tax/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve(response)
      } else {
        reject(response)
      }
    }, api.form(formData, files), 'PATCH')
  })
}

export const deleteWithholdingTax = (id: ID): Promise<void> => {
  return new Promise((resolve, reject) => {
    api.request(`account/withholding-tax/${id}`, (response: any, success: boolean) => {
      if (success) {
        resolve()
      } else {
        reject(response)
      }
    }, {}, 'DELETE')
  })
}

// Dashboard Summary interfaces
export interface DashboardSummaryResponse {
  totalAmount: number
  monthlyChange: number
  accounts: AccountSummary[]
  chartData: ChartDataItem[]
}

export interface AccountSummary {
  id: number
  name: string
  code: string
  gatewayType: 'bank' | 'cash' | 'e_wallet' | 'advance'
  balance: number
  monthlyChange: number
  bankName?: string
  accountNo?: string
  logo?: string
  color: string
  desc?: string
  value?: string // formatted balance string
}

export interface ChartDataItem {
  label: string
  value: number
  color: string
  gatewayType: string
}

// Cash Flow Calendar interfaces
export interface CashFlowCalendarParams {
  year: number
  month: number
  accountIds?: number[]
}

export interface CashFlowCalendarResponse {
  summary: {
    totalIn: { count: number; amount: number }
    totalOut: { count: number; amount: number }
    netAmount: number
  }
  dailyData: Record<string, CashFlowDayData>
}

export interface CashFlowDayData {
  date: string
  in: { count: number; amount: number }
  out: { count: number; amount: number }
  net: number
  transactions?: TransactionSummary[]
}

export interface TransactionSummary {
  id: number
  type: 'in' | 'out'
  amount: number
  description: string
  contactName: string
  paymentGatewayName: string
}

// Cheque Summary interfaces
export interface ChequeSummaryResponse {
  receivedCheques: {
    count: number
    totalAmount: number
    readyToDeposit: number
  }
  paymentCheques: {
    count: number
    totalAmount: number
    readyToPay: number
  }
}

// Dashboard API Functions
export const getDashboardSummary = (): Promise<DashboardSummaryResponse> => {
  return new Promise((resolve, reject) => {
    api.request('account/finance/dashboard/summary', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    })
  })
}

export const getCashFlowCalendar = (params: CashFlowCalendarParams): Promise<CashFlowCalendarResponse> => {
  return new Promise((resolve, reject) => {
    api.request('account/finance/cash-flow/calendar', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    }, params)
  })
}

export const getChequeSummary = (): Promise<ChequeSummaryResponse> => {
  return new Promise((resolve, reject) => {
    api.request('account/finance/cheque/summary', (data: any, success: boolean) => {
      if (success) {
        resolve(data)
      } else {
        reject(data)
      }
    })
  })
}
